import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { User, Landmark, Camera, Save, Pencil } from "lucide-react";
import Owners from "@/pages/Owners";
import { AddressCard } from "@/components/AddressCard";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [me, setMe] = useState(null);
  const EXTRA = [["whatsapp", "WhatsApp number", "+9198XXXXXXXX"], ["city", "City / District", "e.g. Kolkata"], ["dob", "Date of birth", ""], ["facebook", "Facebook link", "https://facebook.com/..."], ["instagram", "Instagram link", "https://instagram.com/..."]];
  const [form, setForm] = useState({ name: "", phone: "", whatsapp: "", city: "", dob: "", bio: "", facebook: "", instagram: "", bank_name: "", account_number: "", ifsc: "", upi_id: "" });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/profile");
    setMe(data);
    setForm({ name: data.name || "", phone: data.phone || "", whatsapp: data.whatsapp || "", city: data.city || "", dob: data.dob || "", bio: data.bio || "", facebook: data.facebook || "", instagram: data.instagram || "", bank_name: data.bank_name || "", account_number: data.account_number || "", ifsc: data.ifsc || "", upi_id: data.upi_id || "" });
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile", form);
      if (photo) {
        const fd = new FormData();
        fd.append("photo", photo);
        await api.post(`/members/${user.id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Profile saved");
      setPhoto(null); await refreshUser(); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const photoUrl = me?.photo_path ? `${API}/members/${me.id}/photo` : null;

  return (
    <div>
      <PageHeader eyebrow="Account" title="My Profile" subtitle={`${user?.member_no ? `Member #${user.member_no} · ` : ""}Your ID, photo and bank account for commission payouts`} />
      {user?.role !== "owner" && <div className="p-4 sm:p-8 pb-0"><AddressCard client={api} /></div>}
      {user?.role === "owner" && (
        <div className="p-4 sm:p-8 pb-0" data-testid="profile-owners-section">
          <p className="eyebrow text-gold mb-3">All Company Partners — share, P&amp;L, business &amp; joining links</p>
          <Owners embedded />
        </div>
      )}
      <div className="p-4 sm:p-8 max-w-2xl space-y-5">
        <Card className="p-5 bg-card/40 border-gold/20" data-testid="profile-id-card">
          <div className="flex items-center gap-4">
            <div className="relative">
              {photoUrl ? (
                <img src={photoUrl} alt={me?.name} className="w-20 h-20 rounded-full object-cover ring-2 ring-gold/50" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-primary/50 border border-gold/30 flex items-center justify-center text-2xl font-serif text-gold">{(me?.name || "?")[0]}</div>
              )}
            </div>
            <div className="min-w-0">
              <p className="font-serif font-semibold text-white text-lg flex items-center gap-2"><User className="w-4 h-4 text-gold" /> {me?.name}</p>
              <Badge variant="outline" className="border-gold/40 text-gold capitalize mt-1">{me?.role}</Badge>
              {me?.referral_code && <p className="text-[11px] text-muted-foreground font-mono mt-1.5">Referral code: <span className="text-gold">{me.referral_code}</span></p>}
              {me?.phone && <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{me.phone}</p>}
            </div>
          </div>
        </Card>

        <Card className="p-5 bg-card/40 border-border/60" data-testid="profile-photo-card">
          <p className="font-serif font-semibold text-white flex items-center gap-2 mb-3"><Camera className="w-4 h-4 text-gold" /> Profile Picture</p>
          <div className="flex items-center gap-3">
            <Button type="button" variant="outline" data-testid="profile-photo-btn" onClick={() => fileRef.current?.click()} className="border-gold/40 text-gold gap-2">
              <Camera className="w-4 h-4" /> {photo ? photo.name : "Choose photo"}
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            {photo && <span className="text-[11px] text-emerald-300">Will upload on Save</span>}
          </div>
        </Card>

        <Card className="p-5 bg-card/40 border-border/60" data-testid="profile-edit-card">
          <p className="font-serif font-semibold text-white flex items-center gap-2 mb-3"><Pencil className="w-4 h-4 text-gold" /> Personal Details</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-muted-foreground">Full name</Label><Input data-testid="profile-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" /></div>
            <div><Label className="text-xs text-muted-foreground">Mobile / WhatsApp</Label><Input data-testid="profile-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60 font-mono" placeholder="+9198XXXXXXXX" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            {EXTRA.map(([k, l, ph]) => (
              <div key={k}><Label className="text-xs text-muted-foreground">{l}</Label>
                <Input data-testid={`profile-${k}-input`} type={k === "dob" ? "date" : "text"} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-card/60" placeholder={ph} /></div>
            ))}
          </div>
          <div className="mt-3"><Label className="text-xs text-muted-foreground">About me</Label>
            <Textarea data-testid="profile-bio-input" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 bg-card/60 min-h-[70px]" placeholder="Experience, languages, areas you serve…" /></div>
          <p className="text-[10px] text-muted-foreground mt-2">Members sign in with this mobile number — keep it correct. Only you, the company and the admin can change your profile.</p>
        </Card>

        <Card className="p-5 bg-card/40 border-border/60" data-testid="profile-bank-card">
          <p className="font-serif font-semibold text-white flex items-center gap-2 mb-3"><Landmark className="w-4 h-4 text-gold" /> Bank Account (for commission payout)</p>
          <div className="space-y-3">
            <div><Label className="text-xs text-muted-foreground">Bank name</Label><Input data-testid="bank-name-input" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. State Bank of India" /></div>
            <div><Label className="text-xs text-muted-foreground">Account number</Label><Input data-testid="account-number-input" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} className="mt-1 bg-card/60 font-mono" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-muted-foreground">IFSC</Label><Input data-testid="ifsc-input" value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} className="mt-1 bg-card/60 font-mono" placeholder="SBIN0001234" /></div>
              <div><Label className="text-xs text-muted-foreground">UPI ID (optional)</Label><Input data-testid="upi-input" value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })} className="mt-1 bg-card/60 font-mono" placeholder="name@upi" /></div>
            </div>
          </div>
        </Card>

        <Button data-testid="profile-save-btn" onClick={save} disabled={saving} className="bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
          <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Profile"}
        </Button>
      </div>
    </div>
  );
}
